/**
 * @fileoverview Select and text change handlers for corporate identity fields.
 * @module components/AppointmentRegistrationSections/CorporateIdentity/useCorporateIdentityChangeHandlers
 */
import { useCallback } from "react";

/**
 * @param {Object} options
 * @param {Function} options.handleInputChange
 * @param {Function} options.handleFieldChange
 * @param {Object} options.descriptions
 * @param {string} options.currentLanguage
 * @param {Function} options.setDescriptions
 * @param {Function} options.setSectorEconomico
 * @param {Function} options.setSubsectors
 * @param {Function} options.setSubSectorSCIAN
 * @param {Function} options.setBranches
 * @param {Function} options.setRamaSCIAN
 * @param {Function} options.setGrupoEmpresarial
 * @param {Function} options.setRazonSocial
 * @param {Function} options.setActividadGeneralDeLaEmpresa
 * @param {Function} options.setAmbitoOperaciones
 * @param {Function} options.setTamanoDeLaEmpresa
 */
export const useCorporateIdentityChangeHandlers = ({
  handleInputChange,
  handleFieldChange,
  descriptions,
  currentLanguage,
  setDescriptions,
  setSectorEconomico,
  setSubsectors,
  setSubSectorSCIAN,
  setBranches,
  setRamaSCIAN,
  setGrupoEmpresarial,
  setRazonSocial,
  setActividadGeneralDeLaEmpresa,
  setAmbitoOperaciones,
  setTamanoDeLaEmpresa,
}) => {
  const handleValidatedFieldChange = useCallback(
    (fieldName, value) => {
      handleFieldChange(fieldName, value);
      handleInputChange(fieldName, value);
    },
    [handleFieldChange, handleInputChange],
  );

  const handleDescriptionChange = useCallback(
    (value) => {
      const newDescriptions = { ...descriptions, [currentLanguage]: value };
      setDescriptions(newDescriptions);
      handleInputChange("descripcionDeLaEmpresa", newDescriptions);
      handleFieldChange("description", value);
    },
    [
      currentLanguage,
      descriptions,
      handleFieldChange,
      handleInputChange,
      setDescriptions,
    ],
  );

  const handleSectorEconomicoChange = useCallback(
    (selectedOption) => {
      setSectorEconomico(selectedOption._id);
      handleInputChange("sectorEconomico", selectedOption._id);
      setSubsectors(selectedOption.subSectores ?? []);
    },
    [handleInputChange, setSectorEconomico, setSubsectors],
  );

  const handleSubSectorChange = useCallback(
    (selectedOption) => {
      setSubSectorSCIAN(selectedOption._id);
      handleInputChange("subSectorSCIAN", selectedOption._id);
      setBranches(selectedOption.ramas ?? []);
      setRamaSCIAN(null);
    },
    [handleInputChange, setBranches, setRamaSCIAN, setSubSectorSCIAN],
  );

  const handleRamaChange = useCallback(
    (selectedOption) => {
      setRamaSCIAN(selectedOption._id);
      handleInputChange("ramaSCIAN", selectedOption._id);
    },
    [handleInputChange, setRamaSCIAN],
  );

  const handleGrupoEmpresarialChange = useCallback(
    (selectedOption) => {
      setGrupoEmpresarial(selectedOption._id);
      handleInputChange("grupoEmpresarial", selectedOption._id);
    },
    [handleInputChange, setGrupoEmpresarial],
  );

  const handleRazonSocialChange = useCallback(
    (value) => {
      setRazonSocial(value);
      handleInputChange("razonSocial", value);
    },
    [handleInputChange, setRazonSocial],
  );

  const handleActividadGeneralChange = useCallback(
    (selectedOption) => {
      setActividadGeneralDeLaEmpresa(selectedOption.value);
      handleInputChange("actividadGeneralDeLaEmpresa", selectedOption.value);
    },
    [handleInputChange, setActividadGeneralDeLaEmpresa],
  );

  const handleAmbitoOperacionesChange = useCallback(
    (selectedOption) => {
      setAmbitoOperaciones(selectedOption.value);
      handleInputChange("ambitoOperaciones", selectedOption.value);
    },
    [handleInputChange, setAmbitoOperaciones],
  );

  const handleTamanoDeLaEmpresaChange = useCallback(
    (selectedOption) => {
      setTamanoDeLaEmpresa(selectedOption.value);
      handleInputChange("tamanoDeLaEmpresa", selectedOption.value);
    },
    [handleInputChange, setTamanoDeLaEmpresa],
  );

  return {
    handleValidatedFieldChange,
    handleDescriptionChange,
    handleSectorEconomicoChange,
    handleSubSectorChange,
    handleRamaChange,
    handleGrupoEmpresarialChange,
    handleRazonSocialChange,
    handleActividadGeneralChange,
    handleAmbitoOperacionesChange,
    handleTamanoDeLaEmpresaChange,
  };
};
